import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, ExternalLink, Check } from 'lucide-react';
import { OWASP_LLM_CHECKED_COUNT } from '../data/owaspLlmChecklist';

const SITE = 'https://githubmate.ai';

interface Tool {
  name: string;
  url: string;
  free: string;
  best: string;
  description: string;
  highlights: string[];
  compareSlug?: string; // links to /compare/githubmate-vs-<slug>
}

// Honest, factual listing. GitHubMate is #1 for the "free / OWASP LLM Top 10 /
// AI-generated code" angle this page targets — others are described fairly.
const tools: Tool[] = [
  {
    name: 'GitHubMate',
    url: 'https://githubmate.ai/',
    free: 'Free, no signup',
    best: 'Best free scanner for AI-generated code & OWASP LLM Top 10',
    description:
      `GitHubMate is a free, client-side security scanner purpose-built for AI-generated ("vibe-coded") code. It delivers heuristic OWASP LLM Top 10 (2025) signals (${OWASP_LLM_CHECKED_COUNT} of 10 categories) plus full OWASP Top 10:2021 coverage, secret scanning, live CVE lookup and IaC checks in about 30 seconds — entirely in the browser, with no account or installation.`,
    highlights: [
      `Heuristic OWASP LLM Top 10 (2025) signals — ${OWASP_LLM_CHECKED_COUNT} of 10 categories`,
      'Dedicated vibe-code / AI-code risk engine',
      '30+ secret patterns + Shannon entropy detection',
      'Live CVE lookup via OSV.dev across 8 ecosystems',
      'SBOM export (JSON/CSV) — free, no signup',
    ],
  },
  {
    name: 'Snyk',
    url: 'https://snyk.io/',
    free: 'Free tier + paid',
    best: 'Best for enterprise remediation & automated fix PRs',
    description:
      'Snyk is a developer-security platform covering SCA, SAST, container and IaC scanning with deep CI/CD and IDE integrations and automated fix pull requests. Strong for org-wide policy enforcement at scale.',
    highlights: ['Automated fix PRs', 'SCA + SAST + container + IaC', 'CI/CD & IDE integrations'],
    compareSlug: 'snyk',
  },
  {
    name: 'SonarQube / SonarCloud',
    url: 'https://www.sonarsource.com/',
    free: 'Community (free) + paid',
    best: 'Best for continuous code quality in CI',
    description:
      'SonarQube is a mature static-analysis platform focused on code quality, technical debt and security hotspots across many languages, run in CI or self-hosted, with pull-request decoration and quality gates.',
    highlights: ['Code quality & smells', 'Quality gates in CI', 'Broad language coverage'],
    compareSlug: 'sonarqube',
  },
  {
    name: 'Semgrep',
    url: 'https://semgrep.dev/',
    free: 'Open-source CLI + paid',
    best: 'Best for custom, rule-based SAST',
    description:
      'Semgrep is a fast, rule-based static analysis engine where teams write or import rules to match code patterns, with a CLI and a paid cloud platform. Great for organization-specific security rules.',
    highlights: ['Custom rule authoring', 'Large community rule registry', 'Scriptable CLI for CI'],
    compareSlug: 'semgrep',
  },
  {
    name: 'CodeRabbit',
    url: 'https://coderabbit.ai/',
    free: 'Free tier + paid',
    best: 'Best for AI pull-request reviews',
    description:
      'CodeRabbit is an AI code-review assistant that comments on pull requests with summaries and inline suggestions, accelerating human review. It complements a dedicated security scanner rather than replacing one.',
    highlights: ['AI PR reviews & summaries', 'Inline suggestions', 'GitHub/GitLab integration'],
    compareSlug: 'coderabbit',
  },
  {
    name: 'Codacy',
    url: 'https://www.codacy.com/',
    free: 'Free for open source + paid',
    best: 'Best for automated quality dashboards',
    description:
      'Codacy is an automated code-quality and security platform that aggregates many open-source analyzers, with dashboards, pull-request checks and coverage tracking across many languages.',
    highlights: ['Aggregates multiple analyzers', 'Quality dashboards', 'PR checks & coverage'],
  },
  {
    name: 'DeepSource',
    url: 'https://deepsource.com/',
    free: 'Free tier + paid',
    best: 'Best for autofix & continuous analysis',
    description:
      'DeepSource provides continuous static analysis for quality, security and performance issues with autofix capabilities and CI integration across popular languages.',
    highlights: ['Autofix transforms', 'Continuous analysis', 'CI integration'],
  },
];

const BestTools: React.FC = () => {
  const url = `${SITE}/best-ai-code-analysis-tools`;
  const title = 'Best AI Code Analysis Tools (2026) — Free & Paid, Compared';
  const description =
    'The best AI code analysis tools in 2026, compared. From the free OWASP LLM Top 10 scanner GitHubMate to Snyk, SonarQube, Semgrep and CodeRabbit — features, pricing and best use cases.';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${SITE}/#website` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'ItemList',
        '@id': `${url}#list`,
        name: 'Best AI Code Analysis Tools (2026)',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: tools.length,
        itemListElement: tools.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: t.name,
          url: t.url,
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-charcoal-bg min-h-full">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">Best AI Code Analysis Tools</span>
          </nav>

          <header className="mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Best AI Code Analysis Tools (2026)
            </h1>
            <p className="text-xl text-gray-600">
              A practical, up-to-date comparison of the leading AI code analysis and security tools —
              free and paid. Whether you're auditing AI-generated code for OWASP LLM Top 10 risks or
              gating quality in CI, here's what to use and when.
            </p>
          </header>

          <section className="prose max-w-none mb-10">
            <p className="text-gray-700 leading-relaxed">
              "AI code analysis" now spans two needs: scanning code <em>for</em> security and quality
              issues (including risks specific to AI-generated code), and using AI <em>to</em> review
              code. The tools below cover both. For a free, no-signup scan focused on AI-generated
              code and the OWASP LLM Top 10, GitHubMate is the fastest place to start.
            </p>
          </section>

          <ol className="space-y-6 mb-12 list-none p-0">
            {tools.map((t, i) => (
              <li key={t.name} className="card">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {i + 1}. {t.name}
                  </h2>
                  <span className="text-xs font-medium bg-blue-50 text-blue-700 px-2 py-1 rounded whitespace-nowrap">
                    {t.free}
                  </span>
                </div>
                <p className="text-sm font-semibold text-blue-700 mb-3">{t.best}</p>
                <p className="text-gray-700 mb-4">{t.description}</p>
                <ul className="space-y-1 mb-4">
                  {t.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-gray-700 text-sm">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  {t.name === 'GitHubMate' ? (
                    <Link to="/" className="btn-primary inline-flex items-center text-sm">
                      <Shield className="h-4 w-4 mr-2" /> Scan free
                    </Link>
                  ) : (
                    <a
                      href={t.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary inline-flex items-center text-sm"
                    >
                      Visit site <ExternalLink className="h-3 w-3 ml-1.5" />
                    </a>
                  )}
                  {t.compareSlug && (
                    <Link
                      to={`/compare/githubmate-vs-${t.compareSlug}`}
                      className="inline-flex items-center text-sm text-blue-600 hover:underline font-medium px-2 py-2"
                    >
                      GitHubMate vs {t.name.split(' ')[0]} →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>

          <section className="rounded-3xl border border-charcoal-border bg-gradient-to-br from-lime/15 via-charcoal-card to-accent-teal/10 p-8 text-center text-gray-900">
            <h2 className="text-2xl font-bold mb-3">Start with a free scan</h2>
            <p className="text-muted max-w-2xl mx-auto mb-6">
              GitHubMate scans any GitHub repository for OWASP LLM Top 10 risks, secrets, CVEs and IaC
              misconfigurations in about 30 seconds — free, no signup.
            </p>
            <Link
              to="/"
              className="btn-primary inline-flex items-center py-3 px-6"
            >
              <Shield className="h-5 w-5 mr-2" /> Scan a repository free
            </Link>
          </section>
        </div>
      </div>
    </>
  );
};

export default BestTools;
